import { Typography } from 'antd';

import LinkToContactUs from '../../components/PrivacyAndTerms/LinkToContactUs';

import styles from './styles.module.scss';

const { Title } = Typography;

function LinkToPrivacy({ sectionId, sectionName }) {
  return (
    <a
      href={sectionId ? `/auth/privacy#${sectionId}` : '/auth/privacy'}
      target="_blank"
      rel="noopener noreferrer"
      className="font-bold">
      {sectionName ? `Privacy Policy ${sectionName}` : 'Privacy Policy'}
    </a>
  );
}

function TermsContent() {
  return (
    <div className={styles['privacy-terms-content']}>
      <Title level={1} className="mb-4">
        Terms and Conditions
      </Title>
      <p className="font-bold">Kraal Ltd</p>
      <p className="font-bold">Last Updated: 15 October 2025</p>
      <p>
        These Terms and Conditions (“Terms”) govern your access to and use of the Kraal web application, related
        services, and IoT Devices (“Service”), provided by Kraal Ltd (“we,” “us,” or “our”), a company registered in
        England and Wales (Company No: 10361693) at 7 Bell Yard, Westminster, London, WC2A 2JR, United Kingdom. By
        accessing or using the Service, including creating an account, subscribing to a free trial or paid plan, or
        ordering IoT Devices (Livestock Tracking Devices or LoRa Gateways), you confirm you are acting in the course of
        a business, trade, or profession, and not as a consumer. If you do not agree to these Terms, you must not use
        the Service or purchase our IoT Devices. This document should be read alongside our <LinkToPrivacy /> for full
        context on data and service use. Nothing in these Terms excludes mandatory rights under applicable laws.
      </p>
      <Title level={3} className="mb-2">
        Summary of Key Terms
      </Title>
      <ul>
        <li>The Service is for business use only, by users authorized to bind their business.</li>
        <li>
          You may create an Account, access a free trial, subscribe to paid plans, or order IoT Devices, subject to
          these Terms.
        </li>
        <li>We own all intellectual property in the Service and IoT Devices.</li>
        <li>
          The Service and IoT Device purchases are governed by English law, with disputes resolved in English courts.
        </li>
        <li>
          Contact us at <LinkToContactUs /> for support or inquiries.
        </li>
      </ul>
      <ol className={styles['ordered-list']}>
        <li>
          <Title level={3} className="mb-2">
            Acceptance of Terms
          </Title>
          <p>
            By clicking “Accept” during account creation, subscription, or IoT Device purchase, or by using the Service,
            you confirm:
          </p>
          <ul>
            <li>You are at least 18 years old and have authority to bind your business to these Terms.</li>
            <li>
              You agree to these Terms, our <LinkToPrivacy />, and any additional terms notified to you.
            </li>
          </ul>
          <p>
            We may update these Terms with 30 days’ notice via email or in-app notification. Continued use or purchase
            constitutes acceptance. Contact us at <LinkToContactUs /> if unsure about business use.
          </p>
        </li>
        <li>
          <Title level={3} className="mb-2">
            Definitions
          </Title>
          <ul>
            <li>
              <strong>Account</strong>: A registered user account on the Service.
            </li>
            <li>
              <strong>Affiliate</strong>: Any entity controlling, controlled by, or under common control with a party.
            </li>
            <li>
              <strong>Business Day</strong>: A day other than a Saturday, Sunday, or public holiday in England.
            </li>
            <li>
              <strong>Content</strong>: Data, text, images, or materials uploaded or submitted by you, including data
              from IoT Devices.
            </li>
            <li>
              <strong>Force Majeure Event</strong>: Events beyond our reasonable control, such as acts of God, war, or
              government actions, excluding routine maintenance.
            </li>
            <li>
              <strong>IoT Devices</strong>: Hardware devices provided by us, including Livestock Tracking Devices and
              LoRa Gateways.
            </li>
            <li>
              <strong>Livestock Tracking Devices</strong>: IoT devices to track and monitor livestock location and
              metrics, transmitting only non-personal animal data.
            </li>
            <li>
              <strong>LoRa Gateways</strong>: Outdoor long-range gateways (may include solar panels/batteries) to
              facilitate data transmission, processing only non-personal telemetry data.
            </li>
            <li>
              <strong>Material Breach</strong>: A significant violation of these Terms, including non-payment, unlawful
              use, or use outside permitted purposes.
            </li>
            <li>
              <strong>Personal Data</strong>: As defined under applicable data protection laws (e.g., UK GDPR).
            </li>
            <li>
              <strong>Subscription</strong>: A free trial or paid access plan to the Service.
            </li>
            <li>
              <strong>User</strong>: A business or individual acting on behalf of a business using the Service or
              purchasing IoT Devices.
            </li>
          </ul>
        </li>
        <li>
          <Title level={3} className="mb-2">
            Scope of Services
          </Title>
          <p>
            Kraal Ltd provides B2B services, including access to our web application and IoT Devices for livestock
            management and monitoring. Data handling is detailed in our <LinkToPrivacy />.
          </p>
        </li>
        <li>
          <Title level={3} className="mb-2">
            Business Use Only
          </Title>
          <p>
            The Service and IoT Devices are provided solely for business use in agriculture or related industries. You
            confirm you are not a consumer, and consumer rights legislation (e.g., UK Consumer Contracts Regulations)
            does not apply.
          </p>
        </li>
        <li>
          <Title level={3} className="mb-2">
            User Accounts
          </Title>
          <p>
            You may create an Account with accurate information and must keep credentials secure. You may authorize
            employees or contractors to access your Account but remain responsible for their compliance. Notify us
            immediately of unauthorized access at <LinkToContactUs />. We may suspend or terminate your Account for
            Material Breaches.
          </p>
        </li>
        <li id="subscriptions">
          <Title level={3} className="mb-2">
            Subscriptions, Free Trials, and IoT Device Purchases
          </Title>
          <ul>
            <li>
              <strong>Accounts and Free Trials</strong>: Free trials are for evaluation, subject to feature/duration
              limits specified at registration. We may terminate trials at our discretion.
            </li>
            <li>
              <strong>Paid Subscriptions</strong>: Plans auto-renew unless cancelled via Account settings or by emailing
              contact@kraal.farmwith 14 days’ notice. We will notify you 7 days before renewal of your right to cancel.
              See{' '}
              <a href="#sla" className="font-bold">
                Service Level Agreement
              </a>{' '}
              for uptime and support details.
            </li>
            <li>
              <strong>IoT Device Purchases</strong>: Orders for Livestock Tracking Devices or LoRa Gateways are subject
              to pricing, delivery, and device-specific terms provided at purchase. Title passes upon full payment; risk
              upon delivery. See <LinkToPrivacy sectionId="personal-data" sectionName="Section 2" /> for payment data
              handling.
            </li>
            <li>
              <strong>Promotions</strong>: Discounts or incentives are subject to specific terms at the time of offer.
            </li>
            <li>
              <strong>Payments and Refunds</strong>: Fees are payable in advance in GBP, non-refundable except as
              required by law or for defective IoT Devices (Section 12). You are responsible for taxes (e.g., VAT). We
              may change fees with 30 days’ notice; overdue payments accrue interest at 1.5% per month or the maximum
              allowed by law. Payments are processed via Stripe, subject to their terms.
            </li>
          </ul>
        </li>
        <li id="client-responsibilities">
          <Title level={3} className="mb-2">
            Client Responsibilities
          </Title>
          <p>You shall:</p>
          <ul>
            <li>
              Ensure proper installation and maintenance of IoT Devices, including suitable outdoor environments for
              LoRa Gateways (e.g., power supply maintenance) and regular inspection of Livestock Tracking Devices.
            </li>
            <li>
              Comply with applicable laws, including the Product Security and Telecommunications Infrastructure Act
              2022.
            </li>
            <li>
              Report issues promptly to <LinkToContactUs />.
            </li>
          </ul>
          <p>
            Security obligations align with <LinkToPrivacy sectionId="data-security" sectionName="Section 8" />.
          </p>
        </li>
        <li id="user-content">
          <Title level={3} className="mb-2">
            User Content and Data Ownership
          </Title>
          <p>
            You own Content, including non-personal animal and telemetry data from IoT Devices, and warrant it complies
            with applicable laws and the Service’s agricultural purposes. We may remove breaching Content, notifying you
            where feasible unless prohibited by law. You may appeal removals within 14 days. We may use anonymized IoT
            data for service improvement, as per <LinkToPrivacy sectionId="purposes" sectionName="Section 4" />. You
            grant us a worldwide, royalty-free licence to use, store, process, and display your Content only as
            necessary to provide and improve the Service.
          </p>
        </li>
        <li>
          <Title level={3} className="mb-2">
            Intellectual Property
          </Title>
          <p>
            We own all intellectual property rights in the Service and IoT Devices, including designs, software, and
            firmware. Feedback you provide becomes our property without compensation, but such use is non-exclusive.
          </p>
        </li>
        <li>
          <Title level={3} className="mb-2">
            Prohibited Conduct
          </Title>
          <p>You must not:</p>
          <ul>
            <li>Upload malicious code or obscene, offensive content.</li>
            <li>Impersonate others.</li>
            <li>Engage in fraudulent or bribery-related activities (e.g., violating UK Bribery Act 2010).</li>
            <li>
              Use the Service or IoT Devices in violation of intellectual property, data protection, export control
              laws, or for non-agricultural purposes.
            </li>
          </ul>
        </li>
        <li>
          <Title level={3} className="mb-2">
            Termination
          </Title>
          <ul>
            <li>
              We may suspend or terminate access immediately for: (a) Material Breach, (b) non-payment, or (c)
              legal/security risks.
            </li>
            <li>
              You may terminate Subscriptions per Section 6. IoT Device purchases are non-cancellable once shipped,
              except as required by law or under Section 12.
            </li>
            <li>
              Upon termination, your licence ends, and you must cease use. We provide 30 days to export Content in a
              structured format (e.g., CSV), after which it may be deleted per{' '}
              <LinkToPrivacy sectionId="data-retention" sectionName="Section 7" />.
            </li>
            <li>Sections 7, 8, 9, 10, 12–21 survive termination.</li>
          </ul>
        </li>
        <li>
          <Title level={3} className="mb-2">
            Disclaimers and IoT Device Warranty
          </Title>
          <p>
            The Service and IoT Devices are provided “as is.” We warrant that IoT Devices will be free from
            manufacturing defects for 12 months from delivery, subject to proper use and maintenance (Section 7). If
            defective, we will repair, replace, or refund at our discretion (contact <LinkToContactUs />
            ). This warranty does not cover misuse, neglect, improper installation, unauthorized modifications, battery
            degradation, or environmental damage. Security measures align with{' '}
            <LinkToPrivacy sectionId="data-security" sectionName="Section 8" />. We disclaim all other warranties,
            including fitness for purpose, except as required by law.
          </p>
        </li>
        <li>
          <Title level={3} className="mb-2">
            Support and Maintenance
          </Title>
          <p>
            We provide firmware updates and technical support for IoT Devices, as outlined in order terms or{' '}
            <a href="#sla" className="font-bold">
              Service Level Agreement
            </a>
            . Report issues within 30 days of detection to <LinkToContactUs />.
          </p>
        </li>
        <li>
          <Title level={3} className="mb-2">
            Limitation of Liability
          </Title>
          <p>To the maximum extent permitted by law:</p>
          <ul>
            <li>We are not liable for indirect, incidental, or consequential damages (e.g., loss of profits, data).</li>
            <li>
              Our total liability in any 12-month period shall not exceed fees paid for the Service and IoT Devices in
              that period.
            </li>
            <li>
              Nothing limits liability for death, personal injury caused by negligence, fraud, or non-excludable
              liabilities.
            </li>
          </ul>
        </li>
        <li>
          <Title level={3} className="mb-2">
            Indemnification
          </Title>
          <p>
            You shall indemnify us against claims, losses, or costs (including legal fees, capped at reasonable amounts)
            arising from your use, Content, or breach of these Terms, with prompt notice required. We shall indemnify
            you against claims arising from our infringement of third-party intellectual property rights.
          </p>
        </li>
        <li id="data-protection">
          <Title level={3} className="mb-2">
            Data Protection
          </Title>
          <p>
            Data from IoT Devices (animal location and telemetry) is exclusively non-personal and not subject to UK
            GDPR. Any Personal Data uploaded by you is processed per our <LinkToPrivacy />. We act as a processor for
            such Personal Data, and clients must ensure compliance as controllers. Upon request, we will enter a DPA
            compliant with UK GDPR Article 28.
          </p>
        </li>
        <li>
          <Title level={3} className="mb-2">
            Notices
          </Title>
          <p>Notices must be in writing:</p>
          <ul>
            <li>
              To us: <LinkToContactUs /> or 7 Bell Yard, Westminster, London, WC2A 2JR, United Kingdom.
            </li>
            <li>To you: via your Account email.</li>
          </ul>
          <p>Notices are deemed received the next Business Day.</p>
        </li>
        <li>
          <Title level={3} className="mb-2">
            Governing Law and Dispute Resolution
          </Title>
          <p>
            These Terms are governed by the laws of England and Wales, with exclusive jurisdiction in English courts.
            For efficiency, disputes may be resolved via arbitration in London under LCIA rules, if mutually agreed.
          </p>
        </li>
        <li>
          <Title level={3} className="mb-2">
            Force Majeure
          </Title>
          <p>
            We are not liable for delays or failures caused by Force Majeure Events, except for warranty obligations
            (Section 12). You remain liable for payments.
          </p>
        </li>
        <li>
          <Title level={3} className="mb-2">
            Export and Compliance
          </Title>
          <p>
            You agree not to use the Service or IoT Devices in violation of applicable export control or sanctions laws
            (e.g., US EAR/ITAR).
          </p>
        </li>
        <li>
          <Title level={3} className="mb-2">
            Miscellaneous
          </Title>
          <ul>
            <li>
              These Terms and the <LinkToPrivacy /> constitute the entire agreement.
            </li>
            <li>Invalid provisions do not affect the remainder.</li>
            <li>
              You may not assign these Terms without our consent, except in corporate restructuring (with notice). We
              may assign freely.
            </li>
            <li>No waiver is effective unless in writing.</li>
            <li>No third-party rights exist under the Contracts (Rights of Third Parties) Act 1999.</li>
          </ul>
        </li>
      </ol>
      <section id="sla">
        <Title level={3} className="mb-2">
          Service Level Agreement (SLA)
        </Title>
        <ul>
          <li>
            <strong>Uptime</strong>: We target 99.9% web application uptime, excluding scheduled maintenance (notified 7
            days in advance).
          </li>
          <li>
            <strong>Support</strong>: Technical support via <LinkToContactUs /> with response times of 24 hours
            (Business Days) for critical issues, 48 hours for others.
          </li>
          <li>
            <strong>Remedies</strong>: If uptime falls below 99.9% in a month, you may request a credit (up to 10% of
            monthly fees) via <LinkToContactUs />.
          </li>
        </ul>
      </section>
      <p className="font-bold">
        Contact: <LinkToContactUs />
      </p>
    </div>
  );
}

export default TermsContent;
